import {
  ExecuteWithdrawalRequest,
  WithdrawalPreviewRequest,
  WithdrawalSettlementDto,
} from '@/features/dashboard/bookingChanges/types/dto/WithdrawalDto.ts';
import { fetchPrivateApi, fetchPublicApi } from '@/lib/api/apiServiceBase.ts';
import { BookingChangeDetailDto } from '@/features/dashboard/bookingChanges/types/dto/BookingChangeDetailDto.ts';
import { BookingChangeListDto } from '@/features/dashboard/bookingChanges/types/dto/BookingChangeListDto.ts';
import { isOpenChangeStatus } from '@/features/dashboard/bookingChanges/types/dto/BookingChangeStatus.utils.ts';
import { PatchBookingChangeRequest } from '@/features/dashboard/bookingChanges/types/dto/PatchBookingChangeRequest.ts';
import { ResolveBookingChangeRequest } from '@/features/dashboard/bookingChanges/types/dto/ResolveBookingChangeRequest.ts';
import { ChangePreviewDto } from '@/features/dashboard/bookingChanges/types/dto/ChangePreviewDto.ts';
import { ChangeSchedulePreviewDto } from '@/features/dashboard/bookingChanges/types/dto/ChangeSchedulePreviewDto.ts';
import { BookingChangePublicDto } from '@/features/dashboard/bookingChanges/types/dto/BookingChangePublicDto.ts';
import { AcceptBookingChangeRequest } from '@/features/dashboard/bookingChanges/types/dto/AcceptBookingChangeRequest.ts';
import { RejectBookingChangeRequest } from '@/features/dashboard/bookingChanges/types/dto/RejectBookingChangeRequest.ts';

/**
 * Serwis modułu zmian rezerwacji — w całości na prawdziwych endpointach.
 *
 * Rachunek rezerwacji tu nie mieszka: składa go `hooks/useBookingBill.ts`
 * z `GET /bookings/{id}`, `GET /bookings/{id}/bill`, wyborów uczestników,
 * katalogu wyjazdu i `POST …/transactions/all`.
 */

const bookingChangesApi = {
  /**
   * Podgląd rozliczenia rezygnacji — nic nie zapisuje, więc wolno go wołać po
   * każdej zmianie wyboru osób i potrącenia.
   */
  previewWithdrawal: async (
    bookingId: string,
    request: WithdrawalPreviewRequest,
  ): Promise<WithdrawalSettlementDto> =>
    await fetchPrivateApi<WithdrawalSettlementDto>(
      'POST',
      `bookings/${bookingId}/withdrawal/preview`,
      request,
    ),

  /**
   * Wprowadzenie rezygnacji. Backend zakłada paczkę `WITHDRAWAL` i od razu ją
   * wprowadza, więc nie ma tu ani szkicu, ani akceptacji klienta — i nie
   * wolno tego zrobić, gdy rezerwacja ma otwartą paczkę.
   */
  executeWithdrawal: async (
    bookingId: string,
    request: ExecuteWithdrawalRequest,
  ): Promise<WithdrawalSettlementDto> =>
    await fetchPrivateApi<WithdrawalSettlementDto>(
      'POST',
      `bookings/${bookingId}/withdrawal`,
      request,
    ),

  /** Lista paczek rezerwacji, od najnowszej. Niesie też szkice. */
  getChanges: async (bookingId: string): Promise<BookingChangeListDto[]> =>
    await fetchPrivateApi<BookingChangeListDto[]>(
      'GET',
      `bookings/${bookingId}/changes`,
    ),

  getChange: async (
    bookingId: string,
    changeId: string,
  ): Promise<BookingChangeDetailDto> =>
    await fetchPrivateApi<BookingChangeDetailDto>(
      'GET',
      `bookings/${bookingId}/changes/${changeId}`,
    ),

  /**
   * Otwarta paczka rezerwacji albo `null`.
   *
   * Dwa żądania, bo `GET …/changes/open` nie istnieje: otwartą paczkę wybiera
   * się z listy po statusie, a pozycje niesie dopiero ekran szczegółów. Reguła
   * „co najwyżej jedna otwarta” jest po stronie backendu, więc pierwsze
   * trafienie jest jedynym.
   */
  getOpenDraft: async (
    bookingId: string,
  ): Promise<BookingChangeDetailDto | null> => {
    const changes = await bookingChangesApi.getChanges(bookingId);
    const open = changes.find((change) => isOpenChangeStatus(change.status));
    return open ? await bookingChangesApi.getChange(bookingId, open.id) : null;
  },

  /** Tworzy szkic albo zwraca istniejący otwarty (operacja idempotentna). */
  createOrGetDraft: async (
    bookingId: string,
  ): Promise<BookingChangeDetailDto> =>
    await fetchPrivateApi<BookingChangeDetailDto>(
      'POST',
      `bookings/${bookingId}/changes`,
    ),

  /**
   * Podmienia **całą** zawartość szkicu. Żądanie musi nieść komplet pozycji —
   * pominięta sekcja kasuje pozycje tego typu.
   */
  patchDraft: async (
    bookingId: string,
    changeId: string,
    request: PatchBookingChangeRequest,
  ): Promise<BookingChangeDetailDto> =>
    await fetchPrivateApi<BookingChangeDetailDto>(
      'PATCH',
      `bookings/${bookingId}/changes/${changeId}`,
      request,
    ),

  /** Odrzuca paczkę („Odrzuć zmiany” w modalu wyjścia z edycji). */
  cancelDraft: async (
    bookingId: string,
    changeId: string,
  ): Promise<BookingChangeDetailDto> =>
    await fetchPrivateApi<BookingChangeDetailDto>(
      'POST',
      `bookings/${bookingId}/changes/${changeId}/cancel`,
    ),

  /** Wprowadza paczkę albo wysyła ją do akceptacji klienta (krok W2). */
  resolveDraft: async (
    bookingId: string,
    changeId: string,
    request: ResolveBookingChangeRequest,
  ): Promise<BookingChangeDetailDto> =>
    await fetchPrivateApi<BookingChangeDetailDto>(
      'POST',
      `bookings/${bookingId}/changes/${changeId}/resolve`,
      request,
    ),

  /** Ponowna wysyłka linku do paczki czekającej na klienta (e-mail i SMS); paczka się nie zmienia. */
  resendChange: async (bookingId: string, changeId: string): Promise<void> =>
    await fetchPrivateApi<void>(
      'POST',
      `bookings/${bookingId}/changes/${changeId}/resend`,
    ),

  /**
   * Rachunek, który paczka postawi, i różnica wobec tego, który stoi —
   * w trzech ziarnistościach (paczka, osoba, wiersz).
   *
   * Działa **wyłącznie dla paczki otwartej** (`DRAFT`/`PENDING_CLIENT`); poza
   * tym 400. Potrafi też odmówić walidacją transportu — tak samo jak
   * `GET /changes/{changeId}`, którego `financialDelta` idzie tą samą ścieżką.
   */
  getPreview: async (
    bookingId: string,
    changeId: string,
  ): Promise<ChangePreviewDto> =>
    await fetchPrivateApi<ChangePreviewDto>(
      'GET',
      `bookings/${bookingId}/changes/${changeId}/preview`,
    ),

  /**
   * Co dopłata z paczki zrobi z harmonogramem rat — policzone tym samym
   * planem, który wykona wprowadzenie paczki. Te same warunki odmowy co
   * `getPreview`.
   */
  getSchedulePreview: async (
    bookingId: string,
    changeId: string,
  ): Promise<ChangeSchedulePreviewDto> =>
    await fetchPrivateApi<ChangeSchedulePreviewDto>(
      'GET',
      `bookings/${bookingId}/changes/${changeId}/schedule-preview`,
    ),

  /**
   * Paczka oczekująca na decyzję klienta — `null`, gdy nic nie czeka.
   *
   * Pyta o nią podsumowanie rezerwacji przy każdym wejściu, tym samym tokenem
   * i slugiem, którymi czyta samo podsumowanie; brak paczki jest zwykłym stanem
   * strony, więc backend odpowiada pustką, a nie 404.
   */
  getPublicChangeByToken: async (
    orgSlug: string,
    token: string,
  ): Promise<BookingChangePublicDto | null> =>
    (await fetchPublicApi<BookingChangePublicDto | null>(
      'GET',
      `public/organizers/${orgSlug}/booking-summary/change?token=${encodeURIComponent(token)}`,
    )) ?? null,

  /** Akceptacja zmian przez klienta; rozjazd kwoty kończy się HTTP 409. */
  acceptPublicChange: async (
    orgSlug: string,
    changeId: string,
    token: string,
    request: AcceptBookingChangeRequest,
  ): Promise<BookingChangePublicDto> =>
    await fetchPublicApi<BookingChangePublicDto>(
      'POST',
      `public/organizers/${orgSlug}/booking-summary/change/${changeId}/accept?token=${encodeURIComponent(token)}`,
      request,
    ),

  /** Odrzucenie zmian przez klienta („Odrzucam zmiany”), z opcjonalnym komentarzem dla organizatora. */
  rejectPublicChange: async (
    orgSlug: string,
    changeId: string,
    token: string,
    request: RejectBookingChangeRequest,
  ): Promise<BookingChangePublicDto> =>
    await fetchPublicApi<BookingChangePublicDto>(
      'POST',
      `public/organizers/${orgSlug}/booking-summary/change/${changeId}/reject?token=${encodeURIComponent(token)}`,
      request,
    ),
};

export default bookingChangesApi;
